import React, { useEffect, useRef, useState } from 'react';
import type { AxiosError } from 'axios';

import { ArrowLeftOutlined, FilePdfOutlined, DownloadOutlined } from '@ant-design/icons';

import {
    Button,
    Card,
    Row,
    Col,
    Divider,
    Tag,
    Typography,
    message,
    notification,
    Empty,
    Form,
    Descriptions,
    Table,
    Alert,
} from 'antd';
import { useHistory } from 'react-router-dom';

import InvoiceStore from 'stores/InvoiceStore';
import { ColumnProps } from 'antd/es/table';
import { Price } from 'components/Display';
import { IInvoice, IInvoiceLineItemProps, IInvoicePayability } from 'types/types';
import FourOhFour from 'pages/404';
import UIStore from '@/stores/UIStore';

import PaymentConfirmed from 'components/Invoices/PaymentConfirmed';
import InvoicePreview from 'components/Invoices/Preview';

import { SquarePaymentForm, ErrorRender } from '@partsbadger/library';
import { SQUARE_APP_ID, SQUARE_ENVIRONMENT_URL, SQUARE_LOCATION_ID } from '@partsbadger/utils';

import PortalLoading from '../../../components/PortalLoading';

import FileDownload from 'js-file-download';

import { observer } from 'mobx-react';

interface IProps {
    match: {
        params: {
            id: string;
        };
    };
}

interface ICreditCardInfo {
    credit_card_token: string;
}

const { Title } = Typography;

const InvoiceDetails: React.FC<IProps> = props => {
    const invoiceId = props.match.params.id;
    const [detailData, setDetailData] = useState<IInvoice>();
    const [line_items, setLineItems] = useState<IInvoiceLineItemProps[]>([]);
    const [payability, setPayability] = useState<IInvoicePayability | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingButton, setLoadingButton] = useState<boolean>(false);
    const [disableButton, setDisableButton] = useState<boolean>(false);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [confirmedAmount, setConfirmedAmount] = useState<number>(0);
    const [preview, setPreview] = useState<boolean>(false);

    const history = useHistory();
    const buttonReference = useRef<HTMLButtonElement>(null);

    const [form] = Form.useForm();

    useEffect(() => {
        let isCurrent = true;

        async function getDetails() {
            setLoading(true);
            setDetailData(undefined);
            setLineItems([]);
            setPayability(null);

            UIStore.setDocumentTitle('Invoice Details');

            try {
                // Fetch invoice details and the live Zoho payability preflight
                // in parallel. The preflight is best-effort: if it fails, we
                // fall back to `total_with_shipping` and keep the pay button
                // enabled so a Zoho outage never blocks a valid customer.
                const [res, payabilityRes] = await Promise.all([
                    InvoiceStore.getInvoiceDetails(invoiceId),
                    InvoiceStore.getPayability(Number(invoiceId)).catch(() => null),
                ]);

                if (!isCurrent) {
                    return;
                }

                setDetailData(res);
                setLineItems(res.line_items);
                setPayability(payabilityRes);
            } catch (error) {
                if (!isCurrent) {
                    return;
                }

                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: <ErrorRender error={error as AxiosError | undefined} />,
                    placement: 'topRight',
                });
            } finally {
                if (isCurrent) {
                    setLoading(false);
                }
            }
        }

        getDetails();

        return () => {
            isCurrent = false;
        };
    }, [invoiceId]);

    const back = () => {
        history.goBack();
    };

    if (!detailData && !loading) {
        return (
            <Card bordered={false}>
                <div className="detail-top-row m-0 bg-white">
                    <div className="sm:w-24 lg:w-12" onClick={() => back()}>
                        <button className="flex items-center border-0 border-white">
                            <ArrowLeftOutlined className="text-base mr-2 mt-1 text-black font-bold" />
                            <span>
                                <strong className="text-sm">Back</strong>
                            </span>
                        </button>
                    </div>
                </div>
                <FourOhFour />
            </Card>
        );
    }

    // Live Zoho balance takes precedence over the PB-local total when
    // available, so a partial payment recorded outside PartsBadger is
    // reflected in the amount the customer is asked to pay.
    const chargeAmount =
        payability?.live_balance ?? Number(detailData?.total_with_shipping ?? 0);
    const invoiceTotal = Number(detailData?.total_with_shipping ?? 0);
    const paymentsMade =
        payability?.live_balance != null && payability.live_balance < invoiceTotal
            ? invoiceTotal - payability.live_balance
            : null;
    const showPayForm =
        detailData?.invoice_status === 'Unpaid' &&
        (payability === null || payability.is_payable);
    const alreadySettled =
        detailData?.invoice_status === 'Unpaid' &&
        payability !== null &&
        !payability.is_payable;

    const columns: ColumnProps<IInvoiceLineItemProps>[] = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: '20%',
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            align: 'center',
        },
        {
            title: 'Unit Price',
            dataIndex: 'list_price',
            key: 'list_price',
            align: 'center',
            render: (text, record) => <Price style={{ fontSize: '14px' }} value={record.list_price} />,
        },
        {
            title: 'Total Price',
            dataIndex: 'total_after_discount',
            key: 'total',
            align: 'right',
            render: (text, record) => <Price style={{ fontSize: '14px' }} value={record.total} />,
        },
    ];

    const invoicePayment = async (payload: ICreditCardInfo) => {
        if (detailData) {
            try {
                // Snapshot the charge amount BEFORE any state updates. After the
                // payment succeeds we re-fetch payability, which returns
                // live_balance=0. Without the snapshot, the reactive chargeAmount
                // would also become 0 and the confirmation modal would show the
                // wrong amount.
                const snapshotAmount = chargeAmount;

                await InvoiceStore.payInvoice(detailData?.id, payload);

                // Re-fetch payability so the "Balance Due" section reflects the
                // post-payment Zoho state (balance=0) without requiring a page
                // reload. Best-effort: a network failure here is non-fatal.
                const updatedPayability = await InvoiceStore.getPayability(Number(invoiceId)).catch(() => null);

                setConfirmedAmount(snapshotAmount);
                setDetailData({ ...detailData, invoice_status: 'Paid' });
                setPayability(updatedPayability);
                setShowModal(true);
                setLoading(false);
                setLoadingButton(false);
            } catch (err: any) {
                setLoading(false);
                setDisableButton(false);
                setLoadingButton(false);

                // 409 = the backend refuses the charge because Zoho considers
                // the invoice not payable (already_paid OR draft). Re-fetch
                // payability so the UI reflects the actual Zoho state instead
                // of blindly forcing invoice_status to 'Paid' (which would be
                // wrong for the 'draft' reason).
                if (err?.response?.status === 409) {
                    const refreshed = await InvoiceStore.getPayability(
                        Number(invoiceId),
                    ).catch(() => null);
                    setPayability(refreshed);

                    // Only mark the invoice as Paid locally when Zoho confirms
                    // it is fully settled — not for draft or other reasons.
                    if (refreshed?.reason === 'already_paid') {
                        setDetailData({ ...detailData, invoice_status: 'Paid' });
                    }

                    notification.warning({
                        duration: 10,
                        message: 'Payment not needed',
                        description:
                            err?.response?.data?.detail ??
                            'This invoice has already been paid.',
                        placement: 'topRight',
                    });
                    return;
                }

                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: <ErrorRender error={err} />,
                    placement: 'topRight',
                });
            }
        }
    };

    const handleDownloadInvoice = async (id: number, name: string) => {
        setLoadingButton(true);

        await InvoiceStore.downloadInvoice(id)
            .then(res => {
                FileDownload(res, `Invoices-${name}.pdf`);
                setLoadingButton(false);
            })
            .catch(error => {
                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                    placement: 'topRight',
                });

                setLoadingButton(false);
            });
    };

    return (
        <>
            <div className="flex justify-start bg-white border-bottom mb-3 pt-3 pb-3" style={{ paddingBottom: '2rem' }}>
                <div className="flex flex-col container">
                    <div className={'flex flex-row'}>
                        <Button
                            type="link"
                            icon={<ArrowLeftOutlined />}
                            onClick={back}
                            className="text-primary"
                        ></Button>
                        <Title level={1} className="mb-2 mr-2">
                            {detailData?.name}
                        </Title>
                    </div>
                    <div className="d-flex flex-row md:ml-5" style={{ columnGap: '6px' }}>
                        <Button icon={<FilePdfOutlined />} onClick={() => setPreview(true)} className="ant-btn-xs">
                            Print
                        </Button>
                        <Button
                            icon={<DownloadOutlined />}
                            onClick={() => detailData && handleDownloadInvoice(detailData?.id, detailData?.name)}
                            className="ant-btn-xs"
                            loading={loadingButton}
                        >
                            Download PDF
                        </Button>
                    </div>
                </div>
            </div>
            <div className="container">
                {loading ? (
                    <PortalLoading />
                ) : (
                    <>
                        <Card bodyStyle={{ paddingTop: '0.5rem' }} className="shadow-sm mb-3">
                            {/* <h1 className="text-gray-800 font-bold text-2xl mb-5">General information</h1> */}

                            <Title level={2}>General information</Title>

                            <Descriptions layout="horizontal" bordered size="small" column={1}>
                                <Descriptions.Item label="Invoice Number">{detailData?.name}</Descriptions.Item>
                                <Descriptions.Item label="PO Number">
                                    <span>{detailData?.purchase_order ?? 'In Review'}</span>
                                </Descriptions.Item>
                                <Descriptions.Item label="Status">
                                    <span className="font-bold">
                                        {detailData?.invoice_status === 'Unpaid' ? (
                                            <Tag color="#FA8C16">{detailData?.invoice_status}</Tag>
                                        ) : (
                                            <Tag color="green">{detailData?.invoice_status}</Tag>
                                        )}
                                    </span>
                                </Descriptions.Item>
                                <Descriptions.Item label="Invoice date">{detailData?.invoice_date}</Descriptions.Item>

                                <Descriptions.Item label="Billing To">
                                    <div className="flex flex-col">
                                        <span className={'value'} style={{ fontSize: '15px' }}>
                                            {detailData?.billing_name}
                                        </span>
                                        <span className={'address-line'}>{detailData?.billing_street}</span>
                                        <span className={'address-line'}>
                                            {detailData?.billing_city} {detailData?.billing_state}{' '}
                                            {detailData?.billing_zip_code}
                                        </span>
                                    </div>
                                </Descriptions.Item>
                                <Descriptions.Item label="Shipping To">
                                    <div className="flex flex-col">
                                        <span className={'value'} style={{ fontSize: '15px' }}>
                                            {detailData?.billing_name}
                                        </span>
                                        <span className={'address-line'}>{detailData?.billing_street}</span>
                                        <span className={'address-line'}>
                                            {detailData?.billing_city} {detailData?.billing_state}{' '}
                                            {detailData?.billing_zip_code}
                                        </span>
                                    </div>
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>

                        <Card bodyStyle={{ paddingTop: '0.5rem' }} className="shadow-sm mb-3">
                            <Title level={2}>Notes</Title>
                            <>
                                <Alert
                                    type="info"
                                    showIcon={true}
                                    description={
                                        <div className="flex flex-col md:flex-row justify-between">
                                            <div className="flex flex-col mb-2">
                                                <Title level={4}>ACH Bank Information:</Title>
                                                <span>First Business Bank</span>
                                                <span>ACH Routing Number: 075905787</span>
                                                <span>ACH Account: 154282768</span>
                                            </div>
                                            <div className="flex flex-col mb-2">
                                                <Title level={4}>Mail Checks to...</Title>
                                                <span>PartsBadger LLC</span>
                                                <span>W66N205 Commerce Court</span>
                                                <span>Cedarburg WI 53012</span>
                                            </div>
                                            <div style={{ maxWidth: 310 }} className="self-center md:pr-10">
                                                <span className="font-bold">
                                                    We appreciate your business and look forward to working with you
                                                    again soon!
                                                </span>
                                            </div>
                                        </div>
                                    }
                                    className="mt-3 mb-3 align-center"
                                />
                            </>
                        </Card>

                        <Row gutter={[16, 8]}>
                            <Col xl={12} span={24}>
                                <Card bodyStyle={{ paddingTop: '0.5rem' }} className="shadow-sm mb-3">
                                    {/* <p className={'label flex items-center'}>Items & Description</p> */}
                                    <Title level={2}>Items & Description</Title>
                                    <Table<IInvoiceLineItemProps>
                                        rowKey={(record: any) => record.id}
                                        pagination={false}
                                        columns={columns}
                                        dataSource={line_items}
                                        locale={{
                                            emptyText: (
                                                <Empty
                                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                    description={
                                                        <span style={{ color: '#000' }}>
                                                            Sorry... This invoice doesn't have parts.
                                                        </span>
                                                    }
                                                />
                                            ),
                                        }}
                                        scroll={window.innerWidth <= 600 ? { x: 600 } : undefined}
                                    />
                                </Card>
                            </Col>
                            <Col xl={12} span={24}>
                                <Card bodyStyle={{ paddingTop: '0.5rem' }} className="shadow-sm mb-3">
                                    <div className="flex flex-col">
                                        <div>
                                            <div className={'flex flex-col justify-between'}>
                                                <div
                                                    className="font-weight-bold my-4"
                                                    style={{
                                                        fontSize: 18,
                                                    }}
                                                >
                                                    Order Summary
                                                </div>
                                                <div className="flex flex-row justify-between mb-1">
                                                    <div className="summary-label text-base">Subtotal</div>
                                                    <div className="summary-value">
                                                        <Price
                                                            style={{ fontSize: '14px' }}
                                                            value={detailData?.subtotal ?? 0}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="flex flex-row justify-between mt-1">
                                                    <div className="summary-label text-base">Discount</div>
                                                    <div className="summary-value">
                                                        <Price
                                                            style={{ fontSize: '14px' }}
                                                            value={detailData?.discount ?? 0}
                                                        />
                                                    </div>
                                                </div>

                                                {Number(detailData?.shipping_cost ?? 0) > 0 && (
                                                    <div className="flex flex-row justify-between mt-1">
                                                        <div className="summary-label text-base">Shipping</div>
                                                        <div className="summary-value">
                                                            <Price
                                                                style={{ fontSize: '14px' }}
                                                                value={detailData?.shipping_cost ?? 0}
                                                            />
                                                        </div>
                                                    </div>
                                                )}

                                                <div
                                                    className="flex flex-row justify-between  mt-2 pt-4"
                                                    style={{
                                                        borderTop: 'solid 1px #d9d9d9',
                                                    }}
                                                >
                                                    <div className="font-bold text-xl">Grand Total</div>
                                                    <div className="font-bold text-xl">
                                                        <Price
                                                            style={{ fontSize: '14px' }}
                                                            value={
                                                                detailData?.total_with_shipping ??
                                                                (Number(detailData?.grand_total ?? 0) + Number(detailData?.shipping_cost ?? 0))
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                                {paymentsMade != null && (
                                                    <>
                                                        <div className="flex flex-row justify-between mt-2">
                                                            <div className="text-gray-500">Payments Made</div>
                                                            <div className="text-red-500">
                                                                (-) <Price style={{ fontSize: '14px' }} value={paymentsMade} />
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="flex flex-row justify-between mt-2 pt-2"
                                                            style={{ borderTop: 'solid 1px #d9d9d9' }}
                                                        >
                                                            <div className="font-bold text-xl">Balance Due</div>
                                                            <div className="font-bold text-xl">
                                                                <Price style={{ fontSize: '14px' }} value={chargeAmount} />
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        {alreadySettled && (
                                            <Alert
                                                type="success"
                                                showIcon={true}
                                                className="mt-3 mb-3"
                                                message={
                                                    payability?.reason === 'draft'
                                                        ? 'This invoice is not yet ready for payment.'
                                                        : 'This invoice has already been paid.'
                                                }
                                                description={
                                                    payability?.reason === 'draft'
                                                        ? 'Please contact support if you believe this is an error.'
                                                        : 'No further action is needed on your side.'
                                                }
                                            />
                                        )}
                                        {showPayForm && (
                                            <div className="w-full">
                                                <>
                                                    <Alert
                                                        type="warning"
                                                        showIcon={true}
                                                        description={
                                                            <div className="flex flex-col">
                                                                <p>
                                                                    -Payment is due on or before the date listed above.
                                                                </p>
                                                                <p> -Payment can be made via Check or ACH.</p>
                                                                <p>
                                                                    -For Credit Card payments after receipt of goods,
                                                                    there will be a 3% processing fee.
                                                                </p>
                                                                <p className="flex flex-col">
                                                                    -Any other questions regarding payments please email{' '}
                                                                    <a
                                                                        href="mailto:tristans@parts-badger.com"
                                                                        className="font-bold"
                                                                        style={{ color: '#FA8C16' }}
                                                                    >
                                                                        tristans@parts-badger.com
                                                                    </a>
                                                                </p>
                                                            </div>
                                                        }
                                                        className="mt-3 mb-3 align-center"
                                                    />
                                                    <div
                                                        className="font-weight-bold my-4"
                                                        style={{
                                                            fontSize: 18,
                                                        }}
                                                    >
                                                        Payment Method
                                                    </div>
                                                    <SquarePaymentForm
                                                        buttonRef={buttonReference}
                                                        app_id={SQUARE_APP_ID}
                                                        location_id={SQUARE_LOCATION_ID}
                                                        script_url={SQUARE_ENVIRONMENT_URL}
                                                        onPayment={sourceId => {
                                                            if (sourceId) {
                                                                setLoadingButton(true);
                                                                form.validateFields()
                                                                    .then(async values => {
                                                                        const payload = {
                                                                            credit_card_token: sourceId,
                                                                        };
                                                                        invoicePayment(payload);
                                                                    })
                                                                    .catch(errorInfo => {
                                                                        form.scrollToField(
                                                                            errorInfo.errorFields[0].name
                                                                        );
                                                                        message.error(
                                                                            'please enter valid credit card number'
                                                                        );
                                                                        setLoadingButton(false);
                                                                    });
                                                            }
                                                        }}
                                                    />
                                                </>

                                                <Button
                                                    id="place-order-btn"
                                                    loading={loadingButton}
                                                    size="large"
                                                    type="primary"
                                                    htmlType="submit"
                                                    block={true}
                                                    onClick={() => {
                                                        if (buttonReference.current) {
                                                            buttonReference.current.click();
                                                        }
                                                    }}
                                                >
                                                    <span className="mr-1">Proceed to Payment</span>
                                                    <Price value={chargeAmount} />
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}

                {showModal && <PaymentConfirmed data={detailData} chargeAmount={confirmedAmount} />}
                {preview && detailData?.id && (
                    <div>
                        <InvoicePreview invoiceId={detailData?.id} onClose={() => setPreview(false)} />
                    </div>
                )}
            </div>
        </>
    );
};

export default observer(InvoiceDetails);
